/**
 * One message in the transcript.
 *
 * Three things this component is careful about.
 *
 * **Per-message direction.** The page direction follows the interface language, but a conversation
 * mixes languages. Each message sets its own `dir` from its own content, so an English question in
 * an Arabic session renders left-to-right inside a right-to-left page without dragging the layout
 * with it.
 *
 * **Four outcomes, four treatments.** An answer, a not-in-knowledge-base refusal, a guardrail
 * refusal and an error are visually distinct. A refusal that looks like a weak answer teaches
 * employees the assistant is unreliable; one that reads as deliberate teaches them when to trust
 * it — so this is a product requirement, not styling taste. An answer is the only outcome with no
 * container at all: it is the content of the page, and the other three are interruptions of it.
 *
 * **The employee's turn and the assistant's turn are shaped differently.** The question is a
 * bubble at the reading end, the answer runs the width of the column behind the assistant's mark.
 * Neither relies on colour alone.
 */

import { useTranslation } from "react-i18next";

import { CopyButton } from "./CopyButton";
import { FeatureCard } from "./FeatureCard";
import { SpeakButton } from "./SpeakButton";
import { FeedbackControls } from "./FeedbackControls";

import { cx, directionOfText, useLanguage } from "@nab/shared";

import avatarUrl from "@nab/shared/assets/nab-ai-avatar.jpg";

import type { ChatMessage } from "../hooks/useChat";
import { AnswerBody } from "./AnswerBody";

export interface MessageProps {
  message: ChatMessage;
  onCitationClick: (index: number) => void;
  /** The conversation, so a request started from this turn can be found again from it. */
  sessionId?: string | null;
  /** Development identity, forwarded to the feedback call. */
  employeeId?: string;
}

/**
 * A message whose direction opposes the page gets a rule on its inline start. Without it an
 * English answer inside an Arabic session reads as broken rather than as a different language.
 */
const MIRRORED = "border-s-2 border-s-borderStrong ps-3";

export function Message({
  message,
  onCitationClick,
  sessionId = null,
  employeeId,
}: MessageProps): JSX.Element {
  const { t } = useTranslation("app");
  const { t: common } = useTranslation();
  const { direction } = useLanguage();

  const contentDirection = message.content
    ? directionOfText(message.content)
    : direction;
  const mirrored = contentDirection !== direction;

  if (message.role === "user") {
    return (
      <article
        className="flex justify-end"
        aria-label={t("chat.yourQuestion")}
      >
        {/* `rounded-se-md` squares the corner nearest the sender's edge, which is what makes the
            bubble point at the person who typed it. Logical rather than `rounded-tr`, so it moves to
            the other side in Arabic instead of pointing at the wrong edge of the page. */}
        <div
          className={cx(
            "max-w-[90%] whitespace-pre-wrap rounded-[24px] rounded-se-md md:max-w-[80%]",
            // The design tints this differently by breakpoint: on a phone the transcript ground is
            // already `surface`, so the bubble needs a lighter grey to separate from it; on a
            // desktop the transcript is white and the bubble takes the blue tint and a border.
            "border border-transparent bg-surfaceSunken px-5 py-4 text-start text-body-md",
            "text-textPrimary lg:border-primaryBorder lg:bg-primarySubtle",
            mirrored && MIRRORED,
          )}
          dir={contentDirection}
        >
          {message.content}
        </div>
      </article>
    );
  }

  const answered = message.outcome === "answered" || message.outcome === null;

  // The accent is reserved for "look here, nothing is wrong" — which is exactly what a
  // not-in-knowledge-base answer is. A guardrail refusal is deliberately inert, an error is red.
  const shell =
    message.outcome === "no_grounding"
      ? "rounded-xl border border-accentBorder border-s-4 border-s-accent bg-accentSubtle p-4"
      : message.outcome === "refused" || message.outcome === "withheld"
        ? "rounded-xl border border-dashed border-borderStrong bg-surfaceSunken p-4"
        : message.outcome === "error"
          ? "rounded-xl border border-statusDanger bg-statusDangerSubtle p-4"
          : "";

  return (
    <article
      className="flex gap-4"
      aria-label={t("chat.assistantAnswer")}
      // Screen readers announce the answer as it streams; `additions text` avoids re-reading
      // the whole message on every token.
      aria-live={message.streaming ? "polite" : "off"}
      aria-atomic="false"
      aria-relevant="additions text"
    >
      {/* The bank's own mark, so the answer is attributed to the institution rather than to a
          character. `aria-hidden` with an empty alt: the article already has an accessible name
          saying whose turn this is, and a second announcement of it reads the label twice. */}
      <span aria-hidden="true" className="mt-1 shrink-0">
        <img
          src={avatarUrl}
          alt=""
          className="h-8 w-8 rounded-full object-cover md:h-9 md:w-9"
        />
      </span>

      <div className={cx("flex min-w-0 flex-1 flex-col gap-3", shell)}>
        {message.outcome === "no_grounding" ? (
          <h3 className="text-heading-sm text-accentStrong">{common("noGrounding.heading")}</h3>
        ) : null}
        {message.outcome === "withheld" ? (
          <h3 className="text-heading-sm text-textMuted">{common("withheld.heading")}</h3>
        ) : null}
        {message.outcome === "error" ? (
          <h3 className="text-heading-sm text-statusDanger">
            {message.auditFailure ? t("chat.notRecordedHeading") : common("errors.generic")}
          </h3>
        ) : null}

        <div
          className={cx(
            "text-start",
            // The answer is the thing being read, so it is set at reading size; a refusal is a
            // short explanation and sits at the weight of one.
            answered ? "text-body-lg text-textPrimary" : "text-body-md text-textMuted",
            mirrored && MIRRORED,
          )}
          dir={contentDirection}
        >
          {answered ? (
            // Blocks, not a pre-wrapped string: the design renders a fee comparison as a table
            // inside the answer, and the prompts now ask for one where it fits.
            <AnswerBody
              text={message.content}
              citations={message.citations}
              onCitationClick={onCitationClick}
              direction={contentDirection}
            />
          ) : (
            <span className="whitespace-pre-wrap">{message.content}</span>
          )}
          {message.streaming ? (
            <span
              aria-hidden="true"
              className="ms-1 inline-block h-4 w-2 animate-pulse bg-primary align-text-bottom motion-reduce:animate-none"
            />
          ) : null}
        </div>

        {message.outcome === "no_grounding" && message.suggestedDepartment ? (
          // Nested on its own surface: this is the one line that tells the employee what to do
          // next, and a sentence appended to a refusal is a sentence nobody acts on.
          <p className="rounded-lg border border-borderSubtle bg-surfaceRaised px-3 py-2 text-body-sm text-textPrimary">
            {common("noGrounding.contactDepartment", {
              department: message.suggestedDepartment,
            })}
          </p>
        ) : null}

        {message.featureOffer ? (
          // UC-B4. Inside the turn, above the action row: it is a thing to act on, and putting it
          // below Copy would bury it under controls for the answer it came with.
          <FeatureCard
            offer={message.featureOffer}
            sessionId={sessionId}
            {...(employeeId ? { employeeId } : {})}
          />
        ) : null}

        {message.regulatoryDisclaimer ? (
          // UC-A3. Its own block with its own weight: a legal notice appended as a trailing
          // sentence is a legal notice nobody reads.
          <p
            role="note"
            className="rounded-e-lg border-s-4 border-s-accent bg-surfaceSunken px-3 py-2 text-body-sm text-textPrimary"
          >
            {message.regulatoryDisclaimer}
          </p>
        ) : null}

        {/* The action row. Copy sits beside the rating controls, as in the design, but outside
            them: `FeedbackControls` replaces itself with a confirmation once a rating is sent, and
            copying an answer has to stay available after you have said what you thought of it.

            Shown on refusals too, and that is the important half. A refusal an employee believes is
            wrong — the assistant declining a question the corpus answers — is the single most
            valuable thing this product can learn, and it was the one outcome with no way to report
            it. Rating was offered only on answers, so a false refusal could be complained about
            everywhere except in the tool that produced it. */}
        {!message.streaming && message.outcome !== "error" ? (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <CopyButton text={message.content} />
            {/* UC-A6, beside Copy and shown on refusals too. A spoken assistant that silently
                skipped "this is not in the knowledge base" would be worse than one that could not
                speak at all. The *answer's* language, not the interface's. */}
            <SpeakButton text={message.content} language={message.language} />
            {/* UC-A5. Only once the server has given us the message id — feedback attaches to that
                id, and it exists only after the turn is on the audit record. */}
            {message.id.startsWith("msg_") ? (
              <FeedbackControls
                messageId={message.id}
                {...(employeeId ? { employeeId } : {})}
              />
            ) : null}
          </div>
        ) : null}

        {message.streaming && !message.content ? (
          <p className="text-body-sm italic text-textMuted">{common("status.thinking")}</p>
        ) : null}

        {/* No footnote list under the answer.
            
            The design puts sources in the panel and nowhere else, and it is right: the list here
            duplicated the panel's content directly beneath it, so an employee saw the same two
            documents twice and the markers in the sentence pointed at the nearer copy. The panel
            is where a citation can carry its passage, version and effective date — the things that
            decide whether an employee may repeat it — and a title alone was never enough. */}
      </div>
    </article>
  );
}
